import React from 'react';
import getConfig from 'next/config';
import SkipLink from '@domain-group/fe-co-skip-link';

import { GetServerSideProps } from 'next';
import {
  ErrorBoundary,
  SellerLanding,
  LandingPageHeader,
  LandingPageFooter,
  Page,
} from '../../components';
import { skipLinkName } from '../../helper/accessibility-utils';
import { AppPageProps } from '../_app';
import { getUserDetails, serializeNextProps } from '../../utils';
import { UserDetailsRedacted } from '../../@types';
import { createDigitalData } from '../../components/head/utils';

const { publicRuntimeConfig } = getConfig();

interface Props extends AppPageProps {
  user: UserDetailsRedacted;
  isEmbeddedApp: boolean;
  trackingDisabled: boolean;
}

const SellLandingPage = ({
  user,
  isEmbeddedApp = false,
  trackingDisabled,
}: Props): JSX.Element => {
  const breadcrumbs = [
    {
      title: 'Sell your home',
      url: `${publicRuntimeConfig.BASE_URL}/sell/`,
    },
  ];

  const digitalData = createDigitalData({
    pageInfo: {
      brand: 'domain',
      pageName: 'Seller Services',
      sysEnv: 'desktop',
      isEmbeddedApp,
      property: {}, // No associated property
    },
    category: {
      primaryCategory: 'Seller Service',
      subCategory1: '',
      pageType: 'Funnel',
    },
    user,
  });

  return (
    <ErrorBoundary>
      <Page
        title="Sell | Property profiles, Price Estimates, Nearby Activity, Agent comparisons and Fee Quotes | Real Estate | Domain"
        description="Information and tools to help you sell your property."
        digitalData={digitalData}
        trackingDisabled={trackingDisabled}
      >
        <>
          <SkipLink anchorName={skipLinkName} />
          {isEmbeddedApp ? null : (
            <LandingPageHeader breadcrumbsData={breadcrumbs} />
          )}
          <SellerLanding
            userId={user.userId}
            sessionId={user.sessionId}
            baseUrl={publicRuntimeConfig.BASE_URL}
            mixpanelToken={publicRuntimeConfig.MIXPANEL_TOKEN}
          />

          {isEmbeddedApp ? null : <LandingPageFooter />}
        </>
      </Page>
    </ErrorBoundary>
  );
};

export default SellLandingPage;

export const getServerSideProps: GetServerSideProps<Props> = async ({
  req,
  res,
}) => {
  const user = getUserDetails(req.user);

  return serializeNextProps<Props>({
    // _app.js props
    serverInfo: res.locals.serverInfo,
    mixpanelToken: publicRuntimeConfig.MIXPANEL_TOKEN,

    // User data
    user,

    // page props
    trackingDisabled:
      typeof req.user?.disableUserTracking === 'boolean'
        ? req.user?.disableUserTracking
        : false,
    isEmbeddedApp:
      typeof req.isEmbeddedApp === 'boolean' ? req.isEmbeddedApp : false,
  });
};
